import {
  Button,
  Dialog,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';
import { useGlobalCore } from '../../../../../core/modules/global';
import { CREATE_CATEGORY_WITH_LIMIT_DIALOG } from '../../../../../core/constants/dialog';
import Spinner from '../../../Spinner';
import { tType, useMasterCore } from '../../../../../core/modules/master';
import { useCompanyCore } from '../../../../../core/modules/company';
import { Type } from '../../../../../core/interfaces/type.interface';
import { Category, ContainerCategory } from '../../../../../core/interfaces/category.interface';

export const TypeLabel = {
  product: 'Producto',
  container: 'Contenedor',
  warehouse: 'Bodega',
  location: 'Ubicación',
  channel: 'Canal',
  acquisition: 'Adquisición',
  reception: 'Recepción',
  containerRecipient: 'Tipo de recipiente',
}
interface Props {
  type: tType;
  types: Type[];
  parent_id: number | null;
  onClose: () => void;
}

interface PostCategory {
  acronym: string;
  name: string;
  description: string;
  type_id?: number;
  parent_id?: number | null;
}

const schema = yup.object().shape({
  acronym: yup.string().test('len', '3 carácteres requeridos', val => val?.length === 3).required('Campo requerido'),
  name: yup.string().required('Campo requerido'),
  description: yup.string().required('Campo requerido'),
  type_id: yup.number().when('parent_id', {
    is: (parent_id: number | null) => !parent_id,
    then: (schema) => schema.required('Campo requerido'),
    otherwise: (schema) => schema.optional(),
  }),
  parent_id: yup.number().nullable().optional(),
});

const CreateDialogWithLimit = (props: Props) => {
  const { type, types, parent_id, onClose } = props;
  const { dialogs, closeDialog } = useGlobalCore();
  const { postCategory, getCategories, containerCategories } = useMasterCore();
  const { companyId } = useCompanyCore();

  const [loading, setLoading]  = useState(false);

  const { control, formState, handleSubmit, resetField, setValue, watch } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
  });
  const { isValid, errors } = formState;

  // Set parent_id when provided and inherit container type
  useEffect(() => {
    if (parent_id && type === 'container') {
      setValue('parent_id', parent_id);
      // Buscar la categoría padre para obtener su tipo de contenedor
      const findCategoryRecursive = (categories: Category[]): ContainerCategory | null => {
        for (const cat of categories) {
          if (cat.id === parent_id) {
            return cat as ContainerCategory;
          }
          const containerCategory = cat as ContainerCategory;
          if (containerCategory.child_categories && containerCategory.child_categories.length > 0) {
            const found = findCategoryRecursive(containerCategory.child_categories);
            if (found) {
              return found;
            }
          }
        }
        return null;
      };
      const parentCategory = findCategoryRecursive(containerCategories);
      if (parentCategory && parentCategory.container_type_id) {
        setValue('type_id', parentCategory.container_type_id);
      }
    }
  }, [parent_id, type, setValue, containerCategories]);

  // Watch parent_id changes to update type when parent changes
  const watchParentId = watch('parent_id');
  useEffect(() => {
    if (watchParentId && type === 'container') {
      // Buscar la categoría padre para obtener su tipo de contenedor
      const findCategoryRecursive = (categories: Category[]): ContainerCategory | null => {
        for (const cat of categories) {
          if (cat.id === watchParentId) {
            return cat as ContainerCategory;
          }
          const containerCategory = cat as ContainerCategory;
          if (containerCategory.child_categories && containerCategory.child_categories.length > 0) {
            const found = findCategoryRecursive(containerCategory.child_categories);
            if (found) {
              return found;
            }
          }
        }
        return null;
      };
      const newParentCategory = findCategoryRecursive(containerCategories);
      if (newParentCategory && newParentCategory.container_type_id) {
        setValue('type_id', newParentCategory.container_type_id);
      } else {
        setValue('type_id', undefined);
      }
    } else if (!watchParentId && type === 'container') {
      setValue('type_id', undefined);
    }
  }, [watchParentId, type, setValue, containerCategories]);

  const resetForm = useCallback(() => {
    resetField('acronym');
    resetField('name');
    resetField('description');
    resetField('type_id');
    resetField('parent_id');
    onClose();
    closeDialog(CREATE_CATEGORY_WITH_LIMIT_DIALOG);
  }, [closeDialog, resetField, onClose]);

  const onSubmit = useCallback(
    (values: any) => {
      if (companyId) {
        setLoading(true);
        const body = type === 'product' ? {
          ...values,
          parent_id: values.parent_id || parent_id,
          product_type_id: !(values.parent_id || parent_id) ? values.type_id : undefined,
        } : {
          ...values,
          parent_id: values.parent_id || parent_id,
          container_type_id: values.type_id || (parent_id ? undefined : values.type_id),
        };

        postCategory(companyId, body, type, () => {
          resetForm();
          getCategories(companyId, type);
          setLoading(false);
        }, () => {
          setLoading(false);
        });
      }
    },
    [companyId, type, postCategory, parent_id, resetForm, getCategories]
  );

  return (
    <Dialog
      classes={{
        paper: 'm-8 sm:m-24 min-w-[350px]',
      }}
      onClose={resetForm}
      open={Array.isArray(dialogs) && dialogs.includes(CREATE_CATEGORY_WITH_LIMIT_DIALOG)}
    >
      <DialogContent>
        <div className="flex flex-auto flex-col py-16 px-0 sm:px-16">
          <div className="flex flex-col">
            <div className="mb-16 flex items-center">
              <Typography className="text-2xl font-bold truncate">
                Crear Categoría de {TypeLabel[type]}
              </Typography>
            </div>
      
            <div className="w-full max-w-3xl mt-20">
              <div className="flex flex-col space-y-32">
                <form noValidate onSubmit={handleSubmit(onSubmit)}>
                  <div className="mb-24">
                    <Controller
                      name="acronym"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Sigla"
                          type="text"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.acronym}
                          helperText={errors?.acronym?.message}
                          required
                          fullWidth
                        />
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="name"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Nombre"
                          type="text"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.name}
                          helperText={errors?.name?.message}
                          required
                          fullWidth
                        />
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="description"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Descripción"
                          type="textarea"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.description}
                          helperText={errors?.description?.message}
                          required
                          fullWidth
                        />
                      )}
                    />
                  </div>

                  {type === 'container' && (
                    <div className="mb-24">
                      <Controller
                        name="parent_id"
                        control={control}
                        render={({ field }) => (
                          <FormControl variant="outlined" fullWidth>
                            <InputLabel id="parent-label">
                              Categoría Padre (Opcional)
                            </InputLabel>
                            <Select
                              {...field}
                              labelId="parent-label"
                              label="Categoría Padre (Opcional)"
                              disabled={loading}
                              value={field.value || parent_id || ''}
                              onChange={(e) => {
                                const value = e.target.value === '' ? null : Number(e.target.value);
                                field.onChange(value);
                              }}
                            >
                              <MenuItem value="">
                                <em>Sin categoría padre</em>
                              </MenuItem>
                              {(() => {
                                // Función recursiva para encontrar todas las categorías en el árbol
                                const getAllCategories = (categories: Category[]): Category[] => {
                                  let allCategories: Category[] = [];
                                  categories.forEach((cat) => {
                                    allCategories.push(cat);
                                    const containerCategory = cat as ContainerCategory;
                                    if (containerCategory.child_categories && containerCategory.child_categories.length > 0) {
                                      allCategories = allCategories.concat(getAllCategories(containerCategory.child_categories));
                                    }
                                  });
                                  return allCategories;
                                };
                                const allContainerCategories = getAllCategories(containerCategories);
                                return allContainerCategories.map((cat) => {
                                  const containerCategory = cat as ContainerCategory;
                                  const displayName = containerCategory.acronym 
                                    ? `(${containerCategory.acronym}) ${containerCategory.name}`
                                    : containerCategory.name;
                                  return (
                                    <MenuItem key={cat.id} value={cat.id}>
                                      {displayName}
                                    </MenuItem>
                                  );
                                });
                              })()}
                            </Select>
                          </FormControl>
                        )}
                      />
                    </div>
                  )}

                  <div className="mb-24">
                    <Controller
                      name="type_id"
                      control={control}
                      render={({ field }) => (
                        <FormControl variant="outlined" fullWidth>
                          <InputLabel id="type-label">
                            Tipo
                          </InputLabel>
                          <Select
                            {...field}
                            labelId="type-label"
                            label="Tipo"
                            disabled={loading || (type === 'container' && (parent_id !== null || !!watchParentId))}
                            required={!parent_id && !watchParentId}
                            value={field.value || ''}
                          >
                            {types.map((type) => (
                              <MenuItem key={type.id} value={type.id}>{type.name}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </div>

                  <div className="flex justify-center">
                    <Button
                      variant="contained"
                      color="secondary"
                      className="w-full"
                      sx={{ borderRadius: 50 }}
                      disabled={!isEmpty(errors) || !isValid || loading}
                      type="submit"
                      size="large"
                    >
                      <Spinner show={loading} />
                      Guardar
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateDialogWithLimit;
